import React, { useCallback, useEffect, useState } from 'react';
import {
 faAirConditioner, faSave, faShareSquare, faSpinner, faUserSlash
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
 Button, Divider, Grid, Step, StepButton, Stepper, Typography
} from '@mui/material';
import {
  ButtonBlue, ButtonBlueLight, ButtonTurquoise,
  CustomTooltip, SkeletonMain, SmallWrapper, Text, TitlePage
} from 'components';
import { subYears } from 'date-fns';
import { useSnackbar } from 'notistack';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useForm, UseFormReturn } from 'react-hook-form';
import { FluidsService, InterventionsService } from 'services';
import styled from 'styled-components';
import { COUNTRY_CODE, INTERVENTION_FORM_STEPS, ROUTES } from 'utils/constants';
import {
  FormHelper, InterventionHelper, parseDate, translate, UserHelper
} from 'utils/helpers';

import { useModal } from 'hooks';
import { ModalState } from 'hooks/ModalProvider';
import { observer } from 'mobx-react-lite';
import { InterventionView } from 'models';
import { EquipmentView, getIdentifier } from 'models/detail/EquipmentView';
import { FormFrontView, getCountryCodeFromForm } from 'models/detail/FormFrontView';
import { InterventionFrontView } from 'models/detail/InterventionFrontView';
import { getFluidWeight } from 'models/detail/RefrigerationUnitView';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import sanitizeHtml from 'sanitize-html';
import { userStore } from 'stores';
import {
  Control, Equipement, EquipementOwner, Fluids, InterventionType, Leaks, Signatures
} from './Sections';

export const FormLine = styled.article`
  display: flex;
  align-items: center;
  & > div {
    flex: 1;
    &:not(:last-of-type) {
      margin-right: 1rem;
    }
  }
`;

const StyledInterventionForm = styled.section`
  position: relative;
  @media (max-width: 768px) {
    & header {
      position: sticky;
      top: 0;
      border-bottom: 1px solid var(--grey-light);
    }
    & footer {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 1rem;
      z-index: var(--zindexBig);
      background: var(--white);
      box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
    }
    & footer .submitButton {
      width: 100%;
    }
  }
  @media (min-width: 769px) {
    & footer {
      margin-top: 4rem;
    }
  }
  & header {
    width: 100%;
    padding: 1rem 0;
    margin-bottom: 1rem;
    background: #ffffff;
    z-index: var(--zindexBig);
    & h2 {
      margin: 0;
      color: var(--blue)a;
    }
  }
  & .MuiStepper-root {
    padding-left: 0;
    padding-right: 0;
    max-width: 100%;
    margin-bottom: 1rem;
    overflow-x: visible;
  }
  & .MuiStep-root {
    margin: 0 4px 12px;
    min-width: auto;
  }
  & .MuiStepLabel-label {
    white-space: normal;
    text-align: center;
  }
  @media (max-width: 768px) {
    & .MuiStepLabel-label {
      font-size: 0.75rem;
    }
  }
`;

const CerfaProgress = styled.div`
  width: 150px;
  margin: 0 auto 1rem auto;
  @media (min-width: 1300px) {
    width: 200px;
    position: absolute;
    left: 10px;
    top: 20px;
  }
`;

const InterventionFormElement = styled.form`
  padding: 1rem 0;
`;

type InputProps = {
  name: string;
  onBlur?: () => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  value: any;
};

type CheckboxProps = {
  checked: boolean;
  name: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

type TextareaProps = {
  name: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  value: any;
};

interface FormStateAdapter<T> {
  errors: Record<string, any>;
  setField: (name: string, value: any) => void;
  values: T;
}

interface FieldHelpers {
  checkbox: {
    name: (name: string) => CheckboxProps;
  };
  text: {
    name: (name: string) => InputProps;
  };
  textarea: {
    name: (name: string) => TextareaProps;
  };
}

export const InterventionForm = observer(() => {
  const { open } = useModal();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const [formCurrentStep, setFormCurrentStep] = useState(-1);
  const [completedSteps, setCompletedSteps] = useState([0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const {
    currentStep = 0,
    intervention,
    needToBeStored = false,
    outOfSyncStatus,
    storedInterventionForm
  } = location.state || {};

  const { id: interventionHashId } = useParams();
  const numberOfSections = INTERVENTION_FORM_STEPS.length;
  const newAppointment = InterventionHelper.getFormattedDateTime(new Date());

  useEffect(() => {
    if (!intervention && (!needToBeStored && !storedInterventionForm)) {
      if (interventionHashId) {
        navigate(`/interventions/${interventionHashId}/detail`);
      } else {
        navigate('/interventions');
      }
    }
    const currentCompletedSteps: number[] = [];
    const setupCurrentCompletedSteps = (maxStep: number) => {
      for (let i = 0; i <= maxStep; i += 1) {
        currentCompletedSteps.push(i);
      }
      setCompletedSteps(currentCompletedSteps);
    };
    if (storedInterventionForm) {
      setFormCurrentStep(storedInterventionForm.currentStep || 0);
      setupCurrentCompletedSteps(storedInterventionForm.currentStep || 0);
    } else if (intervention) {
      setFormCurrentStep(currentStep !== undefined ? currentStep : 0);
      currentStep > 0 && setupCurrentCompletedSteps(currentStep);
    }
  }, [interventionHashId, intervention, storedInterventionForm, needToBeStored, currentStep, navigate]);

  const getDefaultForm = useCallback(() => {
    if (storedInterventionForm) return storedInterventionForm;
    if (intervention) return FormHelper.BACKEND_MAPPER(intervention);
    return {
      ...new FormFrontView(),
      fileNumber: interventionHashId,
      appointment: newAppointment
    };
  }, [intervention, storedInterventionForm, interventionHashId, newAppointment]);

  const methods = useForm<FormFrontView>({
    defaultValues: getDefaultForm()
  });

  const {
 control, handleSubmit, watch, setValue, formState: { errors }
} = methods;
  const formValues = watch();

  const formState: FormStateAdapter<FormFrontView> = {
    values: formValues,
    errors,
    setField: (name: string, value: any) => setValue(name as any, value)
  };

  const textarea = {
    name: (name: string): TextareaProps => ({
      name,
      onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => setValue(name as any, e.target.value),
      value: formValues[name as keyof FormFrontView] || ''
    })
  };

  const getCountryCode = useCallback(
      () => getCountryCodeFromForm(formValues, UserHelper.getDefaultCountry()?.key),
      [formValues]
  );
  const isFrance = useCallback(() => getCountryCode() === COUNTRY_CODE.FR, [getCountryCode]);
  const isGB = useCallback(() => getCountryCode() === COUNTRY_CODE.GB, [getCountryCode]);

  const interventionTypeValues = [
    formValues.assembly,
    formValues.commissioning,
    formValues.modification,
    formValues.maintenance,
    formValues.periodicControl,
    formValues.nonPeriodicControl,
    formValues.dismantling,
    formValues.ui_other
  ];

  useEffect(() => {
    if (needToBeStored) {
      try {
        const newIntervention: FormFrontView = {
          ...formValues,
          currentStep: formCurrentStep,
          inputFluidTotal: Number(formValues.inputFluidTotal),
          outputFluidTotal: Number(formValues.outputFluidTotal)
        };
        InterventionHelper.saveInterventionFormStored(interventionHashId!, newIntervention);
      } catch (error) {
        enqueueSnackbar(error as string, { variant: 'warning' });
      }
    }
  }, [formValues, formCurrentStep, needToBeStored, interventionHashId, enqueueSnackbar]);

  const handleOutOfSync = useCallback(() => {
    open({
      type: 'WARNING',
      text: translate('warnings.intervention.outOfSync', {
        reason: InterventionHelper.getOutOfSyncReason(outOfSyncStatus)
      }),
      buttonCancel: translate('button.cancel'),
      buttonConfirm: translate('button.deleteIntervention'),
      onConfirm: () => InterventionHelper.deleteInterventionStored(interventionHashId!).then(() => {
            enqueueSnackbar(translate('confirms.intervention.delete'), { variant: 'success' });
            navigate(ROUTES.INTERVENTION.LIST);
          })
    });
  }, [interventionHashId, navigate, open, enqueueSnackbar, outOfSyncStatus]);

  const goToNextStep = () => {
    setFormCurrentStep(prev => prev + 1);
    setCompletedSteps(prev => [...prev, formCurrentStep + 1]);
  };

  const checkEquipmentMandatory = () => {
    let errorMessage = '';
    if (formValues.equipment && !formValues.equipment.id) {
      if (!formValues.equipment.model) errorMessage += `- ${translate('errors.equipmentMandatoryModel')}<br/>`;
      if (!formValues.equipment.serialNumber) errorMessage += `- ${translate('errors.equipmentMandatorySerialNumber')}<br/>`;
      if (!formValues.equipment.fluid) errorMessage += `- ${translate('errors.equipmentMandatoryFluid')}<br/>`;
      if (formValues.equipment.fluidWeight === null) errorMessage += `- ${translate('errors.equipmentMandatoryFluidWeight')}<br/>`;
    } else if (!formValues.equipment) {
      errorMessage = translate('errors.equipmentMandatory');
    }
    if (errorMessage) {
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return;
    }
    goToNextStep();
  };

  const checkToolMinDate = () => {
    const minDate = formValues.interventionDate ? parseDate(formValues.interventionDate) : new Date();
    const toolMinDate = subYears(minDate, 1);
    let requiredTool: EquipmentView | null = null;
    if (isFrance() && formValues.leakDetector) requiredTool = formValues.leakDetector;
    if (isGB() && formValues.scale) requiredTool = formValues.scale;
    if (requiredTool) {
      const currentToolDate = parseDate(requiredTool.lastControlDate || requiredTool.acquisitionDate!);
      if (currentToolDate < toolMinDate) {
        open({
          type: 'WARNING',
          text: translate('warnings.intervention.toolControlDate'),
          buttonCancel: translate('button.cancel'),
          buttonAction: translate('button.edit'),
          onConfirm: () => goToNextStep(),
          onAction: () => open({
            type: 'ADD_TOOL',
            onSubmit: (form: EquipmentView) => {
              setValue(isFrance() ? 'leakDetector' : 'scale', form);
              enqueueSnackbar(translate('confirms.toolList.update'), { variant: 'success' });
            },
            defaultValues: requiredTool,
            closeOnSubmit: true,
            forIntervention: true,
            openSecondaryModal: (options: ModalState) => open(options, true)
          })
        });
        return;
      }
    }
    goToNextStep();
  };

  const checkMaintenanceType = () => {
    if (isFrance() && formValues.leaks.some(leak => leak.leakRepaired) && !formValues.maintenance) {
      setValue('maintenance', true);
      open({ type: 'WARNING', text: translate('warnings.intervention.addedMaintenanceType'), onConfirm: () => goToNextStep() });
      return;
    }
    if (Object.values(formValues).slice(0, 7).every(val => !val)) {
      setFormCurrentStep(5);
      setCompletedSteps(prev => [...prev, 5]);
      return;
    }
    goToNextStep();
  };

  const checkFluidManipulations = () => {
    const field = FormHelper.BUILD_FLUID_MANIPULATIONS({
      inputList: formValues.fluidManipulationsInput,
      outputList: formValues.fluidManipulationsOutput,
      inputTotal: formValues.inputFluidTotal,
      outputTotal: formValues.outputFluidTotal
    });
    FluidsService.checkFluidManipulations(field)
        .then(() => goToNextStep())
        .catch(() => {
          if (userStore.isOffline) open({ type: 'WARNING', text: translate('errors.NETWORK_VALIDATION_IMPOSSIBLE'), onConfirm: () => goToNextStep() });
        });
  };

  const checkFluidsValidity = () => {
    let warningMessage = '';

    if (formState.values.equipment) {
      // CHECKS ON FLUIDS MANIPULATIONS OUTPUT
      if (!formState.values.assembly) {
        if (Number(formState.values.outputFluidTotal) < getFluidWeight(formState.values.equipment)
            && formState.values.leaks.length === 0 && formState.values.fluidManipulationsOutput.length > 0) {
          warningMessage += `${translate('warnings.intervention.fluidsNoLeaks')} <br /><br />`;
        } else if (Number(formState.values.outputFluidTotal) > getFluidWeight(formState.values.equipment)) {
          warningMessage += `${translate(
              'warnings.intervention.fluidsOutputMoreThanCapacity',
              { outputFluidTotal: formState.values.outputFluidTotal, fluidWeight: getFluidWeight(formState.values.equipment) }
          )}<br /><br />`;
        }

        if (formState.values.fluidManipulationsOutput.length > 0) {
          // Display warning if the fluid in the manipulation is different from the one in the equipment
          if (formState.values.equipment && formState.values.equipment.fluid
              && formState.values.fluidManipulationsOutput.find(manip => manip.fluid.label !== formState.values.equipment.fluid.label)) {
            const manipWithError = formState.values.fluidManipulationsOutput.find(manip => manip.fluid.label !== formState.values.equipment.fluid.label);
            warningMessage += `${translate(
                'warnings.intervention.fluidsOutputWrongFluid',
                { label: manipWithError.fluid.label, identifier: manipWithError.fluidBottle.identifier, name: formState.values.equipment.fluid.label }
            )} <br /><br />`;
          }
        }
      }

      // CHECKS ON FLUIDS MANIPULATIONS INPUT
      if (!formState.values.dismantling && formState.values.fluidManipulationsInput.length > 0) {
        if (Number(formState.values.inputFluidTotal) < getFluidWeight(formState.values.equipment)) {
          warningMessage += `${translate(
              'warnings.intervention.fluidsInputLessThanCapacity',
              { inputFluidTotal: formState.values.inputFluidTotal, fluidWeight: getFluidWeight(formState.values.equipment) }
          )} <br /><br />`;
        } else if (Number(formState.values.inputFluidTotal) > getFluidWeight(formState.values.equipment)) {
          warningMessage += `${translate(
              'warnings.intervention.fluidsInputMoreThanCapacity',
              { inputFluidTotal: formState.values.inputFluidTotal, fluidWeight: getFluidWeight(formState.values.equipment) }
          )} <br /><br />`;
        }

        if (formState.values.leaks.length > 0 && formState.values.leaks.some(leak => !leak.leakRepaired)) {
          warningMessage += `${translate('warnings.intervention.fluidsInputNoLeakAdded')} <br /><br />`;
        }

        if (formState.values.equipment && formState.values.equipment.fluid
            && formState.values.fluidManipulationsInput.find(manip => manip.fluid.label !== formState.values.equipment.fluid.label)) {
          // Display warning if the fluid in the manipulation is different from the one in the equipment
          const manipWithError = formState.values.fluidManipulationsInput.find(manip => manip.fluid.label !== formState.values.equipment.fluid.label);
          warningMessage += `${translate(
              'warnings.intervention.fluidsInputWrongFluid',
              { label: manipWithError.fluid.label, name: formState.values.equipment.fluid.label }
          )} <br /><br />`;
        }
      }
    }

    if (warningMessage) {
      return open({
        buttonCancel: translate('button.cancel'),
        type: 'WARNING',
        text: warningMessage,
        onConfirm: () => checkFluidManipulations()
      });
    }

    return checkFluidManipulations();
  };

  const handleStepPrevious = () => {
    if (formCurrentStep === 4 && !formValues.periodicControl && !formValues.nonPeriodicControl) {
      setFormCurrentStep(1);
      return;
    }
    if (formCurrentStep === 5 && Object.values(formValues).slice(0, 7).every(val => !val)) {
      setFormCurrentStep(3);
      return;
    }
    setFormCurrentStep(prev => prev - 1);
  };

  const handleStepNext = () => {
    if (formCurrentStep < numberOfSections) {
      switch (formCurrentStep) {
        case 0:
          checkEquipmentMandatory();
          break;
        case 1:
          if (!formValues.periodicControl && !formValues.nonPeriodicControl) {
            setFormCurrentStep(4);
            setCompletedSteps(prev => [...prev, 2, 3, 4]);
          } else goToNextStep();
          break;
        case 2:
          checkToolMinDate();
          break;
        case 3:
          checkMaintenanceType();
          break;
        case 4:
          checkFluidsValidity();
          break;
        default:
          goToNextStep();
      }
    }
  };

  const getWarningMessage = () => {
    let warningMessage = '';
    if (!formValues.equipmentOwner) warningMessage += `<li>${translate('warnings.intervention.mandatoryEquipmentOwner')}</li>`;
    if (!formValues.equipment) warningMessage += `<li>${translate('warnings.intervention.mandatoryEquipment')}</li>`;
    if (!interventionTypeValues.some(Boolean)) warningMessage += `<li>${translate('warnings.intervention.mandatoryInterventionType')}</li>`;
    if (isFrance() && !formValues.leakDetector && formValues.nonPeriodicControl) warningMessage += `<li>${translate('warnings.intervention.mandatoryLeakDetector')}</li>`;
    if (isGB() && !formValues.scale && formValues.nonPeriodicControl) warningMessage += `<li>${translate('warnings.intervention.mandatoryScale')}</li>`;
    return warningMessage;
  };

  const saveIntervention = useCallback(async () => {
    if (!needToBeStored) {
      if (userStore.isOffline) {
        enqueueSnackbar(translate('common.userIsOffline'), { variant: 'error', autoHideDuration: 10000 });
      } else {
        setIsSubmitting(true);
        const completedIntervention: InterventionView = {
          ...intervention!,
          fileNumber: decodeURIComponent(formValues.fileNumber),
          observations: formValues.observations,
          form: FormHelper.BUILD(formValues)
        };
        InterventionsService.finishIntervention(interventionHashId!, completedIntervention)
            .then(async (interventionFromServer) => {
              await InterventionHelper.deleteInterventionStored(interventionHashId!);
              enqueueSnackbar(translate('confirms.intervention.finishedOnline'), { variant: 'success', autoHideDuration: 10000 });
              navigate(`/interventions/${interventionFromServer.hashId}/detail`);
            })
            .catch(() => setIsSubmitting(false));
      }
    } else {
      // Logique pour une intervention stockée
      const interventionDetailStored = await InterventionHelper.loadInterventionDetailStored(interventionHashId!);
      const completedIntervention: InterventionFrontView = {
        ...interventionDetailStored,
        fileNumber: decodeURIComponent(formValues.fileNumber),
        observations: formValues.observations
      };
      const completedForm: FormFrontView = {
        ...formValues,
        currentStep: formCurrentStep,
        inputFluidTotal: Number(formValues.inputFluidTotal),
        outputFluidTotal: Number(formValues.outputFluidTotal)
      };

      if (userStore.isOffline) {
        InterventionHelper.saveInterventionDetailStored(interventionHashId!, {
          ...completedIntervention,
          isWaitingToSubmit: true
        });
        InterventionHelper.saveInterventionFormStored(interventionHashId!, completedForm);
        enqueueSnackbar(translate('confirms.intervention.finishedOffline'), { variant: 'success', autoHideDuration: 10000 });
        navigate(ROUTES.INTERVENTION.LIST);
      } else {
        setIsSubmitting(true);
        InterventionsService.finishIntervention(interventionHashId!, {
          ...completedIntervention,
          form: FormHelper.BUILD(formValues)
        }).then(async (interventionFromServer) => {
          await InterventionHelper.deleteInterventionStored(interventionHashId!);
          enqueueSnackbar(translate('confirms.intervention.finishedOnline'), { variant: 'success', autoHideDuration: 10000 });
          navigate(`/interventions/${interventionFromServer.hashId}/detail`);
        }).catch(() => {
          setIsSubmitting(false);
        });
      }
    }
  }, [enqueueSnackbar, formValues, navigate, interventionHashId, formCurrentStep, intervention, needToBeStored]);

  const saveInterventionProgress = () => {
    setIsSaving(true);

    InterventionHelper.loadInterventionDetailStored(interventionHashId!)
        .then((int) => {
          const currentIntervention: InterventionView = {
            ...int,
            fileNumber: decodeURIComponent(formValues.fileNumber),
            form: FormHelper.BUILD(formValues)
          };

          const currentHashId = currentIntervention.hashId
              || currentIntervention.form.hashId
              || currentIntervention.fileNumber
              || decodeURIComponent(formValues.fileNumber);

          return InterventionsService.updateIntervention(currentHashId, currentIntervention)
              .then(() => enqueueSnackbar(translate('confirms.intervention.saved'), { variant: 'success' }))
              .catch((error) => enqueueSnackbar(error as string, { variant: 'error' }))
              .finally(() => setIsSaving(false));
        });
  };

  const handleInterventionEnd = () => {
    const warningContent = getWarningMessage();
    if (warningContent) {
      open({ type: 'WARNING', title: translate('common.mandatoryField'), text: `<div>${translate('warnings.intervention.mandatoryTitle')} :<ul>${warningContent}</ul></div>` });
      return;
    }
    saveIntervention();
  };

  if ((!storedInterventionForm && !intervention) || formCurrentStep < 0) return <SkeletonMain />;

  const cerfaVersionForSteps = InterventionHelper.getCerfaVersion(formValues.interventionDate);
  const CustomMagnifier: React.FC<{ alt: string, bigSrc: string; src: string; }> = ({ src, alt }) => (
    <TransformWrapper initialScale={1} panning={{ disabled: false }} wheel={{ step: 0.1 }}>
      <TransformComponent>
        <img alt={alt} src={src} style={{ width: '100%', cursor: 'zoom-in' }} />
      </TransformComponent>
    </TransformWrapper>
  );
  const equipmentOwnerForm: UseFormReturn<FormFrontView> = methods;
  const equipmentForm = {
    formState,
    isRequired: true,
    textarea
  };
  const interventionTypeForm = {
    control,
    watch,
    setValue,
    countryCode: getCountryCode(),
    interventionTypeValues
  };
  const controlForm = {
    form: methods,
    countryCode: getCountryCode(),
    name: 'control'
  };
  const leaksForm = {
    control,
    setValue,
    watch
  };
  const fluidsForm = {
    form: methods,
    countryCode: getCountryCode()
  };
  const signaturesForm = {
    form: equipmentOwnerForm,
    countryCode: getCountryCode()
  };

  return (
    <StyledInterventionForm>
      <SmallWrapper>
        <header>
          <Grid alignItems="center" container justifyContent="space-between" spacing={1}>
            <Grid item>
              <h2>{`${translate('common.intervention')} n°${formValues.fileNumber}`}</h2>
              {formValues.equipment && <Typography><FontAwesomeIcon className="mr1" icon={faAirConditioner} />{`${translate('common.equipment')} : ${getIdentifier(formValues.equipment)}`}</Typography>}
            </Grid>
            {!userStore.isOffline && needToBeStored && (
              <Grid item>
                <ButtonTurquoise disabled={isSaving || outOfSyncStatus} onClick={saveInterventionProgress}>
                  <FontAwesomeIcon icon={isSaving ? faSpinner : faSave} spin={isSaving} />{translate('button.save')}
                </ButtonTurquoise>
              </Grid>
              )}
          </Grid>
        </header>

        {outOfSyncStatus && <Button style={{ display: 'block', margin: '0 auto 1rem auto' }} onClick={handleOutOfSync}><Grid container spacing={1} style={{ color: 'var(--orange)' }}><Grid item><FontAwesomeIcon icon={faUserSlash} /></Grid><Grid item>{translate('pageInterventionDetail.outOfSync')}</Grid></Grid></Button>}
        {!userStore.isOffline && isFrance() && <CerfaProgress><CustomMagnifier alt="CERFA" bigSrc={`${process.env.PUBLIC_URL}/assets/images/cerfa_progress/${cerfaVersionForSteps}/step${formCurrentStep}-big.jpg`} src={`${process.env.PUBLIC_URL}/assets/images/cerfa_progress/${cerfaVersionForSteps}/step${formCurrentStep}.jpg`} /></CerfaProgress>}

        <Stepper activeStep={formCurrentStep} alternativeLabel sx={{
            overflowX: 'visible',
            flexWrap: 'wrap',
            justifyContent: 'center',
            maxWidth: '100%'
          }}>
          {INTERVENTION_FORM_STEPS.filter((section, idx) => {
              if (!formValues.nonPeriodicControl && !formValues.periodicControl && (idx === 2 || idx === 3)) return false;
              if ([formValues.assembly, formValues.commissioning, formValues.modification, formValues.maintenance, formValues.dismantling, formValues.ui_other].every(t => !t) && idx === 4) return false;
              return true;
            }).map((section, idx) => <Step completed={completedSteps.includes(idx)} disabled={!completedSteps.includes(idx)} key={section.id}><StepButton onClick={() => setFormCurrentStep(idx)}>{translate(section.label)}</StepButton></Step>)}
        </Stepper>

        <InterventionFormElement name="interventionForm" onSubmit={handleSubmit(() => {})}>
          {INTERVENTION_FORM_STEPS[formCurrentStep] && <article>
            <TitlePage data-cy="interventionFormStepTitle">{translate(INTERVENTION_FORM_STEPS[formCurrentStep].title)}{INTERVENTION_FORM_STEPS[formCurrentStep].tooltip && <CustomTooltip text={translate(INTERVENTION_FORM_STEPS[formCurrentStep].tooltip)} />}</TitlePage>
              {[
                <>
                  <EquipementOwner formState={formState} />
                  <Equipement {...equipmentForm} />
                </>,
                <InterventionType key="interventionType" {...interventionTypeForm} />,
                <Control key="control" {...controlForm} />,
                <Leaks key="leaks" {...leaksForm} />,
                <Fluids key="fluids" {...fluidsForm} />,
                <Signatures key="signatures" {...signaturesForm} />
              ][formCurrentStep]}
            </article>}

          <Divider style={{ margin: '1rem 0' }} />
          {formCurrentStep !== numberOfSections - 1 ? (
            <Grid alignItems="center" container justifyContent="space-between">
              {formCurrentStep === 0 ? <Button variant="text" onClick={() => navigate(ROUTES.INTERVENTION.LIST)}>{translate('button.quit')}</Button> : <ButtonBlueLight data-cy="interventionFormPrev" disabled={formCurrentStep === 0} onClick={handleStepPrevious}>{translate('button.previous')}</ButtonBlueLight>}
              <ButtonBlue data-cy="interventionFormNext" onClick={handleStepNext}>{translate('button.next')}</ButtonBlue>
            </Grid>
            ) : (
              <div id="saveInterventionForm">
                {!formValues.operatorSignatureField?.name ? (
                  <Grid alignItems="center" container justifyContent="space-between"><ButtonBlueLight data-cy="interventionFormPrev" onClick={handleStepPrevious}>{translate('button.previous')}</ButtonBlueLight></Grid>
                  ) : (
                    <Grid alignItems="center" container direction="column">
                      <Grid item><Grid alignItems="center" container justifyContent="space-between">{!userStore.isOffline && <img alt={translate('interventionForm.general.thanks')} height="40px" src={`${process.env.PUBLIC_URL}/assets/images/icons/thumbUp.png`} />}<Text fontSize="2rem" fontWeight={600} margin="0 0 0 2rem">{translate('interventionForm.general.thanks')}</Text></Grid></Grid>
                      <ButtonTurquoise className="submitButton" data-cy="inspectionValidate" disabled={isSubmitting || outOfSyncStatus} sx={{ margin: '1rem 0 0 0' }} onClick={handleInterventionEnd}><FontAwesomeIcon icon={isSubmitting ? faSpinner : faShareSquare} spin={isSubmitting} /><Typography dangerouslySetInnerHTML={{ __html: sanitizeHtml(translate('interventionForm.general.finishAndSend')) }} /></ButtonTurquoise>
                    </Grid>
                  )}
              </div>
            )}
        </InterventionFormElement>
      </SmallWrapper>
    </StyledInterventionForm>
  );
});

export default InterventionForm;
